import { ConfigProvider } from "antd";
import type { ThemeConfig } from "antd";
import React, { useEffect } from "react";
import { useUserOrganization } from "./utils/partnerData";

const parentTheme: ThemeConfig = {
    token: {
        fontSize: 18,
    },
    components: {
        Collapse: {
            fontSize: 20,
        },
        Typography: {
            fontSize: 20,
        },
        Layout: {
            headerBg: "white",
            headerColor: "black",
        },
        Form: {
            labelColor: "black",
        },
        Button: {
            algorithm: true,
        },
    },
    hashed: false,
    cssVar: { key: "«r1»" },
};

function applyAppVariant(variant: string) {
    document.documentElement.dataset.variant = variant; // <html data-variant="brandA">
}

/**
 * Control the theme of the application based on the organization. Ant Design relies on the config here to set the
 * colors, font sizes, margins, etc. The CSS variables exposed are used in some of our custom components. These vars
 * are visible in the dev tools under the element styles tab.
 *
 */
function AntDesignConfig(props: { children: React.ReactNode }) {
    const { children } = props;

    const { userOrganization } = useUserOrganization();

    const theme = userOrganization.theme;
    const { fontUrl, headingFontFamily, key } = userOrganization;

    useEffect(() => {
        applyAppVariant(key);
    }, [key]);
    useEffect(() => {
        if (!fontUrl) return;

        const preconnectGoogle = Object.assign(document.createElement("link"), {
            rel: "preconnect",
            href: "https://fonts.googleapis.com",
        });
        const preconnectGstatic = Object.assign(document.createElement("link"), {
            rel: "preconnect",
            href: "https://fonts.gstatic.com",
            crossOrigin: "anonymous",
        });
        const stylesheet = Object.assign(document.createElement("link"), {
            rel: "stylesheet",
            href: fontUrl,
        });

        document.head.append(preconnectGoogle, preconnectGstatic, stylesheet);
        return () => {
            preconnectGoogle.remove();
            preconnectGstatic.remove();
            stylesheet.remove();
        };
    }, [fontUrl]);

    useEffect(() => {
        if (!headingFontFamily) return;

        const style = document.createElement("style");
        style.textContent = `[data-variant="${key}"] h1,
[data-variant="${key}"] h2,
[data-variant="${key}"] h3,
[data-variant="${key}"] h4,
[data-variant="${key}"] h5,
[data-variant="${key}"] h6 { font-family: ${headingFontFamily}; }`;
        document.head.appendChild(style);
        return () => style.remove();
    }, [headingFontFamily, key]);

    return (
        <ConfigProvider theme={parentTheme}>
            <ConfigProvider theme={theme}>{children}</ConfigProvider>
        </ConfigProvider>
    );
}

export default AntDesignConfig;
